'use client';

/* @jsxRuntime classic */
// The Roma Vitest/React 17 setup resolves JSX's jsxDEV helper incorrectly for this package.

import React, { type CSSProperties, type ReactNode, type UIEventHandler, useEffect } from 'react';
import { ChevronLeftIcon } from '@milanote/roma-icons';
import { cx } from '../../../utils/cx';
import { ButtonContent, styles as buttonStyles } from '../button/Button';
import { SheetContextProvider, useSheetContext } from './SheetContext';
import { CLOSING_CLASS } from './sheetStateAttributes';
import type { SheetProps } from './sheetTypes';

const SEMANTIC_NAME_SHEET = 'RomaSheet';
const SEMANTIC_NAME_OVERLAY = 'RomaSheetOverlay';
const SEMANTIC_NAME_PANEL = 'RomaSheetPanel';
const SEMANTIC_NAME_HEADER = 'RomaSheetHeader';
const SEMANTIC_NAME_HEADING = 'RomaSheetHeading';
const SEMANTIC_NAME_CONTENT = 'RomaSheetContent';
const SEMANTIC_NAME_HEADER_BUTTON = 'RomaSheetHeaderButton';
const SEMANTIC_NAME_DONE_BUTTON = 'RomaSheetDoneButton';
const SEMANTIC_NAME_CLOSE_BUTTON = 'RomaSheetCloseButton';
const SEMANTIC_NAME_BACK_BUTTON = 'RomaSheetBackButton';
const SEMANTIC_NAME_NON_DRAGGABLE_AREA = 'RomaSheetNonDraggableArea';

type ChildrenAndClassName = { children: ReactNode; className?: string };
type HeaderButtonProps = {
    className?: string;
    label?: string;
    onClick?: () => void;
};

// Kept mounted until the close transition completes, so the sheet can animate out
const SheetContainer = ({ children, className, style }: ChildrenAndClassName & { style?: CSSProperties }) => {
    const { setIsSheetMounted, sheetContainerRef } = useSheetContext();

    useEffect(() => {
        setIsSheetMounted(true);
    }, []);

    return (
        <div
            className={cx(
                SEMANTIC_NAME_SHEET,
                'pointer-events-none absolute inset-0 z-(--z-index-sheet) overflow-hidden select-none',
                '[-webkit-touch-callout:none]',
                '[--sheet-transition-time:0.5s] [--sheet-transition-easing:cubic-bezier(0.32,0.72,0,1.02)]',
                // Lets the sheet animate a little above the top of the screen rather than stopping dead
                '[--sheet-dragging-height:calc(100%+50px)]',
                className,
            )}
            style={style}
            ref={sheetContainerRef}
            onClick={(event) => event.stopPropagation()}
        >
            {children}
        </div>
    );
};

SheetContainer.displayName = SEMANTIC_NAME_SHEET;

/** A bottom sheet that can be dragged between snap points. Fully controlled — see README.md. */
const SheetRoot = ({ children, className, style, ...behaviourProps }: SheetProps) => {
    const { isOpen } = behaviourProps;

    return (
        <SheetContextProvider {...behaviourProps}>
            {(isSheetMounted: boolean) =>
                (isSheetMounted || isOpen) && (
                    <SheetContainer className={cx(!isOpen && CLOSING_CLASS, className)} style={style}>
                        {children}
                    </SheetContainer>
                )
            }
        </SheetContextProvider>
    );
};

SheetRoot.displayName = SEMANTIC_NAME_SHEET;

/** Transparent by default — give it a background to dim what's underneath. */
const Overlay = ({ className, onClick }: { className?: string; onClick?: () => void }) => {
    const { requestClose } = useSheetContext();

    const onClickHandler = () => {
        onClick?.();
        requestClose();
    };

    return (
        <div
            className={cx(
                SEMANTIC_NAME_OVERLAY,
                'pointer-events-auto absolute inset-0 opacity-0',
                'transition-opacity duration-(--sheet-transition-time) ease-(--sheet-transition-easing)',
                className,
            )}
            onClick={onClickHandler}
        />
    );
};

Overlay.displayName = SEMANTIC_NAME_OVERLAY;

// The drag engine toggles `sheet-dragging`, `sheet-transition`, `sheet-full-height` and
// `content-height` on this element outside of React, so their styles are declared here as variants
const Panel = ({ children, className }: ChildrenAndClassName) => {
    const {
        sheetRef,
        topInset,
        handleSheetTouchStart,
        handleSheetTouchMove,
        handleSheetTouchEnd,
        handleSheetTouchCancel,
    } = useSheetContext();

    return (
        <div
            className={cx(
                SEMANTIC_NAME_PANEL,
                'pointer-events-auto absolute top-full right-0 left-0 flex h-full flex-col',
                'rounded-t-3xl transition-[border-radius] duration-300',
                // At full height the sheet keeps a small radius against the safe area, and squares off without one
                topInset ? 'data-[sheet-full-height]:rounded-t-lg' : 'data-[sheet-full-height]:rounded-t-none',
                // Use the modal background token so sheets match the rest of the app while the colour system rollout is in progress.
                'bg-(--ui-modal-background-primary) shadow-2xl',
                'data-[content-height]:h-auto',
                'data-[sheet-dragging]:h-(--sheet-dragging-height)',
                // Not built from SCROLLABLE_ATTRIBUTE — Tailwind's scanner cannot see through interpolation
                "[&[data-sheet-dragging]_[data-scrollable='true']]:overflow-y-hidden",
                "[&[data-sheet-dragging]_[data-scrollable='true']]:overscroll-y-none",
                'data-[sheet-transition]:transition-[transform] data-[sheet-transition]:duration-(--sheet-transition-time)',
                'data-[sheet-transition]:ease-(--sheet-transition-easing)',
                className,
            )}
            ref={sheetRef}
            onTouchStart={handleSheetTouchStart}
            onTouchMove={handleSheetTouchMove}
            onTouchEnd={handleSheetTouchEnd}
            onTouchCancel={handleSheetTouchCancel}
        >
            {children}
        </div>
    );
};

Panel.displayName = SEMANTIC_NAME_PANEL;

/**
 * The draggable area at the top of the sheet. Can hold a heading, header buttons or anything else.
 */
const Header = ({ children, className }: ChildrenAndClassName) => (
    <div className={cx(SEMANTIC_NAME_HEADER, 'relative h-15 py-xl [--sheet-header-side-space:7.4rem]', className)}>
        {children}
    </div>
);

Header.displayName = SEMANTIC_NAME_HEADER;

const Heading = ({ children, className }: ChildrenAndClassName) => (
    <div
        className={cx(
            SEMANTIC_NAME_HEADING,
            'mx-auto w-[calc(100%-(var(--sheet-header-side-space)*2))] overflow-x-hidden text-center',
            'text-ellipsis whitespace-nowrap',
            'text-display-md',
            className,
        )}
    >
        {children}
    </div>
);

Heading.displayName = SEMANTIC_NAME_HEADING;

/**
 * Shared styling for the buttons pinned to either end of the header.
 *
 * These are plain buttons rather than React Aria ones: they sit on the sheet's touch-drag surface,
 * and press handling that swallows touch events stops the header being draggable.
 */
const sheetHeaderButtonLayoutClasses = cx(
    SEMANTIC_NAME_HEADER_BUTTON,
    'absolute top-0 m-0 h-full cursor-pointer border-0 bg-transparent px-3xl! py-lg! no-underline select-none',
);

const getHeaderButtonClasses = (variantClasses: string) =>
    cx(
        buttonStyles.common.root,
        buttonStyles.sizes.lg.root,
        buttonStyles.sizes.lg.linkRoot,
        variantClasses,
        sheetHeaderButtonLayoutClasses,
    );

const ClosingHeaderButton = ({
    children,
    className,
    label,
    onClick,
    semanticName,
    variantClasses,
}: HeaderButtonProps & {
    children?: ReactNode;
    semanticName: string;
    variantClasses: string;
}) => {
    const { requestClose } = useSheetContext();

    const onClickHandler = () => {
        onClick?.();
        requestClose();
    };

    return (
        <button
            type="button"
            className={cx(getHeaderButtonClasses(variantClasses), semanticName, 'right-0', className)}
            aria-label={label ?? 'Close sheet'}
            onClick={onClickHandler}
        >
            <ButtonContent noTextPadding>{children}</ButtonContent>
        </button>
    );
};

/** The standard orange Done action used in mobile sheet headers. */
const DoneButton = (props: HeaderButtonProps & { children?: ReactNode }) => (
    <ClosingHeaderButton
        {...props}
        semanticName={SEMANTIC_NAME_DONE_BUTTON}
        variantClasses={buttonStyles.colors['link-color'].root}
    >
        {props.children ?? 'Done'}
    </ClosingHeaderButton>
);

DoneButton.displayName = SEMANTIC_NAME_DONE_BUTTON;

/** Closes the sheet with the standard grey close action style. */
const CloseButton = (props: HeaderButtonProps & { children?: ReactNode }) => (
    <ClosingHeaderButton
        {...props}
        semanticName={SEMANTIC_NAME_CLOSE_BUTTON}
        variantClasses={buttonStyles.colors['link-gray'].root}
    >
        {props.children ?? 'Close'}
    </ClosingHeaderButton>
);

CloseButton.displayName = SEMANTIC_NAME_CLOSE_BUTTON;

/** Steps back within a sheet flow. Does not close the sheet — the consumer decides what "back" means. */
const BackButton = ({ className, label, onClick }: HeaderButtonProps & { label: string }) => (
    <button
        type="button"
        className={cx(
            getHeaderButtonClasses(buttonStyles.colors['link-gray'].root),
            SEMANTIC_NAME_BACK_BUTTON,
            // Symmetric padding for the icon; the chevron's ~8px internal dead space lands its visible edge at the 23px inset
            'left-0 flex items-center justify-center px-[1.5rem]',
            className,
        )}
        aria-label={label}
        onClick={onClick}
    >
        <ChevronLeftIcon aria-hidden="true" className="size-6 shrink-0 transition-inherit-all" />
    </button>
);

BackButton.displayName = SEMANTIC_NAME_BACK_BUTTON;

/** The scrollable content area. Resized as the sheet is dragged and as the keyboard appears. */
const Content = ({
    children,
    className,
    onScroll,
}: ChildrenAndClassName & { onScroll?: UIEventHandler<HTMLDivElement> }) => {
    const { sheetContentRef } = useSheetContext();

    return (
        <div
            className={cx(
                SEMANTIC_NAME_CONTENT,
                'overflow-y-scroll overscroll-y-contain pb-(--safe-area-bottom)',
                className,
            )}
            ref={sheetContentRef}
            onScroll={onScroll}
            data-scrollable
        >
            {children}
        </div>
    );
};

Content.displayName = SEMANTIC_NAME_CONTENT;

/** Only needed where the normal scroll/drag detection gets it wrong, e.g. draggable content. */
const NonDraggableArea = ({ children, className }: ChildrenAndClassName) => (
    <div className={cx(SEMANTIC_NAME_NON_DRAGGABLE_AREA, className)} data-sheet-ignore-drags>
        {children}
    </div>
);

NonDraggableArea.displayName = SEMANTIC_NAME_NON_DRAGGABLE_AREA;

type SheetComponent = typeof SheetRoot & {
    Overlay: typeof Overlay;
    Panel: typeof Panel;
    Header: typeof Header;
    Heading: typeof Heading;
    Content: typeof Content;
    DoneButton: typeof DoneButton;
    CloseButton: typeof CloseButton;
    BackButton: typeof BackButton;
    NonDraggableArea: typeof NonDraggableArea;
};

export const Sheet = Object.assign(SheetRoot, {
    Overlay,
    Panel,
    Header,
    Heading,
    Content,
    DoneButton,
    CloseButton,
    BackButton,
    NonDraggableArea,
}) as SheetComponent;
